import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { TopSticky } from "~/components/ui/topSticky";
import { TOAST_MESSAGES } from "~/constants/messages";
import { UserApi } from "~/functions/api/user";
import BackArrowIcon from "~/images/arrow-left_black.svg?react";
import companyLogo from "~/images/reqxign-logo-full.png";
import type { UpdateUser, UserData } from "~/interface/user";
import { setCurrentUserData } from "~/slices/auth";
import type { RootState } from "~/store";
import styles from "./index.module.scss";

// 文字制限の定数
const CHARACTER_LIMITS = {
  firstName: 50,
  lastName: 50,
  displayName: 50,
};

const SignupInvitation: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentUser } = useSelector((state: RootState) => state.auth);
  const [currentStep, setCurrentStep] = useState(1);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [displayName, setDisplayName] = useState("");

  useEffect(() => {
    // Check if user is authenticated
    if (!currentUser?.id) {
      navigate("/signin");
    } else {
      // Initialize user data if available
      setFirstName(currentUser.first_name || "");
      setLastName(currentUser.last_name || "");
      setDisplayName(currentUser.display_name || "");
    }
  }, [currentUser, navigate]);

  const nextStep = () => {
    setCurrentStep((prevStep) => (prevStep < 2 ? prevStep + 1 : prevStep));
  };

  const backStep = () => {
    setCurrentStep((prevStep) => (prevStep > 1 ? prevStep - 1 : prevStep));
  };

  const handleComplete = async () => {
    if (!displayName) {
      toast.error(TOAST_MESSAGES.SIGNUP.DISPLAY_NAME_REQUIRED);
      return;
    }

    const updateBody: UpdateUser = {
      first_name: firstName,
      last_name: lastName,
      display_name: displayName,
    };

    const userApi = new UserApi();
    const response = await userApi.update(currentUser.organization_id, updateBody);

    if (response !== null) {
      toast.success(TOAST_MESSAGES.SIGNUP.SETUP_COMPLETED);
      const updatedUserData: UserData = {
        last_name: lastName,
        first_name: firstName,
        display_name: displayName,
      };
      dispatch(setCurrentUserData(updatedUserData));

      // Redirect to home after a short delay
      setTimeout(() => {
        navigate(`/${currentUser.organization_id}/home`);
      }, 1000);
    } else {
      toast.error(TOAST_MESSAGES.SIGNUP.SAVE_FAILED);
    }
  };

  const handleSkip = () => {
    // Navigate directly to home without saving
    navigate(`/${currentUser.organization_id}/home`);
  };

  return (
    <div className={styles["overlay"]}>
      <div className={styles["modal"]}>
        <img src={companyLogo} alt="Company Logo" />
        <div className={styles["modalBody"]}>
          {currentStep === 1 && <Welcome nextStep={nextStep} />}
          {currentStep === 2 && (
            <UserSettingComponent
              firstName={firstName}
              setFirstName={setFirstName}
              lastName={lastName}
              setLastName={setLastName}
              displayName={displayName}
              setDisplayName={setDisplayName}
              backStep={backStep}
              onComplete={handleComplete}
              onSkip={handleSkip}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const Welcome: React.FC<{ nextStep: () => void }> = ({ nextStep }) => {
  return (
    <div>
      <div className={styles["header-container_welcome"]}>
        <h1>REQXIGNへようこそ！</h1>
      </div>
      <div className={styles["form-description_welcome"]}>
        REQXIGNへようこそ！
        <br />
        まずは、サービスをより便利にお使いいただくための設定を行いましょう。
      </div>
      <div>
        <button
          className={`${styles["save-button"]} ${styles["override-margin"]}`}
          onClick={nextStep}
        >
          次へ
        </button>
      </div>
    </div>
  );
};

interface UserSettingComponentProps {
  firstName: string;
  setFirstName: React.Dispatch<React.SetStateAction<string>>;
  lastName: string;
  setLastName: React.Dispatch<React.SetStateAction<string>>;
  displayName: string;
  setDisplayName: React.Dispatch<React.SetStateAction<string>>;
  backStep: () => void;
  onComplete: () => void;
  onSkip: () => void;
}

const UserSettingComponent: React.FC<UserSettingComponentProps> = ({
  firstName,
  setFirstName,
  lastName,
  setLastName,
  displayName,
  setDisplayName,
  backStep,
  onComplete,
  onSkip,
}) => {
  const [error, setError] = useState<string>("");

  // 文字数チェック
  const lastNameCount = useMemo(() => lastName.length, [lastName]);
  const firstNameCount = useMemo(() => firstName.length, [firstName]);
  const displayNameCount = useMemo(() => displayName.length, [displayName]);

  // 文字数制限を超えているかのチェック
  const hasExceededLimits = useMemo(() => {
    return (
      lastNameCount > CHARACTER_LIMITS.lastName ||
      firstNameCount > CHARACTER_LIMITS.firstName ||
      displayNameCount > CHARACTER_LIMITS.displayName
    );
  }, [lastNameCount, firstNameCount, displayNameCount]);

  const handleComplete = () => {
    if (!displayName) {
      setError("表示名フィールドは必須です。");
      return;
    }

    if (hasExceededLimits) {
      setError("入力された文字数が制限を超えています。");
      return;
    }

    setError("");
    onComplete();
  };

  return (
    <div>
      <div className={styles["header-container"]}>
        <div className={styles["step-header"]}>
          <button onClick={backStep} className={styles["back-button"]}>
            <BackArrowIcon />
          </button>
          <TopSticky content="ステップ2 / 2" />
        </div>
        <h1>ユーザー情報の設定</h1>
      </div>
      <div className={styles["name-container"]}>
        <div className={`${styles["form-group"]}`}>
          <label>姓</label>
          <input
            type="text"
            value={lastName}
            placeholder="ノベル"
            onChange={(e) => setLastName(e.target.value)}
            maxLength={CHARACTER_LIMITS.lastName}
          />
          <div
            className={`${styles["char-counter"]} ${lastNameCount > CHARACTER_LIMITS.lastName ? styles["exceeded"] : ""}`}
          >
            {lastNameCount}/{CHARACTER_LIMITS.lastName}
          </div>
        </div>
        <div className={`${styles["form-group"]}`}>
          <label>名</label>
          <input
            type="text"
            value={firstName}
            placeholder="筋太郎"
            onChange={(e) => setFirstName(e.target.value)}
            maxLength={CHARACTER_LIMITS.firstName}
          />
          <div
            className={`${styles["char-counter"]} ${firstNameCount > CHARACTER_LIMITS.firstName ? styles["exceeded"] : ""}`}
          >
            {firstNameCount}/{CHARACTER_LIMITS.firstName}
          </div>
        </div>
      </div>
      <div className={styles["form-group"]}>
        <label>表示名*</label>
        <input
          type="text"
          value={displayName}
          placeholder="表示名"
          onChange={(e) => setDisplayName(e.target.value)}
          maxLength={CHARACTER_LIMITS.displayName}
          required
        />
        <div
          className={`${styles["char-counter"]} ${displayNameCount > CHARACTER_LIMITS.displayName ? styles["exceeded"] : ""}`}
        >
          {displayNameCount}/{CHARACTER_LIMITS.displayName}
        </div>
      </div>
      {error && <p className={styles["error-message"]}>{error}</p>}
      <div className={styles["button-section"]}>
        <button
          className={`${styles["save-button"]} ${styles["override-margin"]}`}
          onClick={handleComplete}
          disabled={hasExceededLimits}
        >
          設定を完了
        </button>
        <button className={styles["skip-button"]} onClick={onSkip}>
          スキップ
        </button>
      </div>
    </div>
  );
};

export default SignupInvitation;
